/**
 * The message shown after a DXF is analyzed on the Part page.
 *
 * jobq-api's /dxf/analyze and /parts/{id}/analyze_dxf return
 * `geometry_warnings`: [{code, level: 'warning' | 'info', message}] (since
 * 2026-10-13). Before that the page always said "analyzed successfully", even
 * when the outline didn't close and the area it filled in was only a hole
 * (VBMW's latch side plate read 0.43 sq in instead of 56.87): weight and price
 * then came out wrong with nothing on screen to say so.
 *
 *   no_closed_outline         (warning) the area is 0
 *   geometry_outside_outline  (warning) size measured from title block / dims / an open outline
 *   gaps_closed               (info)    tiny drafting gaps were bridged; the file still has them
 *
 * An older API sends no `geometry_warnings`: same message as before.
 */
export const DXF_ANALYZED_OK =
  "DXF analyzed successfully. Area, dimensions, pierce count, and image have been updated.";

export function dxfGeometryWarnings(data) {
  const list = data && Array.isArray(data.geometry_warnings) ? data.geometry_warnings : [];
  return list.filter(w => w && typeof w.message === "string" && w.message.trim());
}

/**
 * `saved`: the existing-part endpoint (/parts/{id}/analyze_dxf) has already
 * written these numbers to the part and re-priced it, so a fix is "correct and
 * Save", not "check before saving".
 */
export function dxfAnalyzedMessage(data, { saved = false } = {}) {
  const warnings = dxfGeometryWarnings(data);
  if (warnings.length === 0) return DXF_ANALYZED_OK;
  const needsCheck = warnings.some(w => w.level !== "info");
  const head = !needsCheck
    ? DXF_ANALYZED_OK
    : saved
      ? "DXF analyzed and its numbers are now saved on the part. CHECK the area and size: if they're wrong, correct them and Save."
      : "DXF analyzed, but CHECK the area and size before saving the part:";
  return [head, ...warnings.map(w => `• ${w.message.trim()}`)].join("\n\n");
}

/**
 * A NEW part's DXF is analyzed twice: /dxf/analyze fills the form, and after
 * POST /parts the page attaches the file with /parts/{id}/analyze_dxf, which
 * writes the DXF's own area / size / pierce count over whatever was saved. When
 * the person corrected those (the warnings ask them to), the corrections were
 * lost. Returns the PATCH body that puts back what they saved, or null.
 * `sent` = the POST /parts payload, `analyzed` = the analyze_dxf answer.
 */
export const DXF_DERIVED_FIELDS = ["solid_profile_area", "size_length", "size_width", "pierce_count"];

export function dxfValuesToKeep(sent, analyzed) {
  if (!sent || !analyzed) return null;
  const keep = {};
  for (const field of DXF_DERIVED_FIELDS) {
    const mine = sent[field];
    const theirs = analyzed[field];
    if (mine === null || mine === undefined || mine === "" || Number.isNaN(Number(mine))) continue;
    if (theirs === null || theirs === undefined || Number.isNaN(Number(theirs))) continue;
    // The form shows 4 decimals of the analysis, so an untouched value differs only past them.
    const tol = field === "pierce_count" ? 0 : 0.0006;
    if (Math.abs(Number(mine) - Number(theirs)) > tol) keep[field] = mine;
  }
  return Object.keys(keep).length ? keep : null;
}

/**
 * Why a sheet part has no usable profile area (`solid_profile_area`), or null when it has one.
 *
 *   'none'  nothing stored: no DXF has been analyzed for the part. On prod 2026-11-04 this was
 *           219 of 221 such parts with no DXF on file at all, most from the March 2025 import
 *           (KC0183 / KC0184 on Jesse's FALSEMOUNTJIGFLOOR cut sheet).
 *   'zero'  a DXF was analyzed and measured 0 (all 17 such parts had a DXF on file): the outline
 *           didn't close, often because of a lead-in line.
 *
 * Either way the cut sheet's material split uses the bounding box (length x width) instead.
 */
export function profileAreaProblem(area) {
  if (area === null || area === undefined) return "none";
  if (typeof area === "string" && area.trim() === "") return "none";
  const n = Number(area);
  if (Number.isNaN(n)) return "none";
  return n === 0 ? "zero" : null;
}
